import React, { useState } from 'react';
import useGetSuppliers, { SupplierDto } from '../Requests/Suppliers/useGetSuppliers';
import { Table } from 'reactstrap';
import { Button } from 'react-bootstrap';
import { customerDisplayName } from '../../Utilities';
import formatPostalAddress from '../Common/PostalAddressDisplay';
import BoldButton from '../Common/BoldButton';
import useRemoveSupplier from '../Requests/Suppliers/useRemoveSupplier';
import { textSatisfiesFilter } from '../FilterInput';
import Confirm from '../Common/Confirm';
import { CreateEditSupplier } from './CreateEditSupplier';
import QueryView from '../Common/QueryView';
import { CenteredSpinner } from '../Common/CenteredSpinner';

type SuppliersTabProps = {
  filterText: string;
  createEditSupplier: { supplier?: SupplierDto } | undefined;
  setCreateEditSupplier: (supplier: { supplier?: SupplierDto } | undefined) => void;
  requestSuccessful: (message: string) => void;
  requestFailed: (err: string) => void;
}

const SuppliersTab = (props: SuppliersTabProps) => {
  const getSuppliers = useGetSuppliers();

  const requestSuccessful = (message: string) => {
    props.requestSuccessful(message);
    getSuppliers.query();
  };

  return <QueryView query={getSuppliers}
    renderLoading={() =>
      <div className="mt-8">
        <CenteredSpinner />
      </div>}
    renderData={suppliers =>
      <Suppliers
        {...props}
        suppliers={suppliers}
        requestSuccessful={requestSuccessful}
        requestFailed={props.requestFailed} />
    } />;
}

const Suppliers = (props: SuppliersTabProps & { suppliers: SupplierDto[] }) => {
  const [supplierToRemove, setSupplierToRemove] = useState<SupplierDto | undefined>();

  const removeSupplier = useRemoveSupplier(supplierToRemove?.id, {
    onSuccess: () => props.requestSuccessful(`Supplier ${supplierToRemove?.name} removed`),
    onError: err => props.requestFailed(err.message),
    onComplete: () => setSupplierToRemove(undefined),
  });

  const containsFilterText = (s: SupplierDto) => {
    const satisfiesFilter = textSatisfiesFilter(props.filterText);
    return satisfiesFilter(s.name) || satisfiesFilter(s.accountNumber)
  };

  const sortAndFilterSuppliers = (suppliers: SupplierDto[]) =>
    suppliers.sort((a, b) => a.name.localeCompare(b.name)).filter(containsFilterText);

  const formatAddress = (supplier: SupplierDto) =>
    formatPostalAddress(
      supplier.addressStreet,
      supplier.addressCity,
      supplier.addressState,
      supplier.addressPostalCode,
      supplier.addressCountry);

  return <Table bordered>
    {
      !!props.createEditSupplier &&
      <CreateEditSupplier
        initSupplier={props.createEditSupplier.supplier}
        onHide={() => props.setCreateEditSupplier(undefined)}
        onSuccess={supplier => {
          props.setCreateEditSupplier(undefined);
          props.requestSuccessful(`Supplier ${supplier.name} ${props.createEditSupplier?.supplier ? 'updated' : 'created'}`);
        }}
      />
    }
    <Confirm
      visible={!!supplierToRemove}
      title='Remove supplier'
      body={`When a supplier is removed it cannot be restored. 
             Are you sure you want to remove ${supplierToRemove?.name}?`}
      isLoading={removeSupplier.isLoading}
      onDismiss={() => setSupplierToRemove(undefined)}
      onConfirm={() => removeSupplier.request()} />
    <thead>
      <tr>
        <th className="col-3">Supplier</th>
        <th className="col-auto">Address</th>
        <th className="col-auto">Email</th>
        <th className="col-1">Actions</th>
      </tr>
    </thead>
    <tbody>
      {sortAndFilterSuppliers(props.suppliers).map(s =>
        <tr key={s.id}>
          <td>
            <Button
              variant="link"
              onClick={() => props.setCreateEditSupplier({ supplier: s })}>
              {customerDisplayName(s.name, s.accountNumber)}
            </Button>
          </td>
          <td>{formatAddress(s)}</td>
          <td>{s.emailAddresses?.join(', ')}</td>
          <td>
            <div className="dropdown">
              <Button className='dropdown-toggle btn-ghost-secondary' variant='link' data-bs-toggle='dropdown' >
                <strong>Actions</strong>
              </Button>
              <div className='dropdown-menu'>
                <BoldButton
                  className='dropdown-item btn-ghost-secondary'
                  onClick={() => props.setCreateEditSupplier({ supplier: s })}>
                  Edit
                </BoldButton>
                <BoldButton
                  className='dropdown-item btn-ghost-secondary'
                  onClick={() => setSupplierToRemove(s)}>
                  Remove
                </BoldButton>
              </div>
            </div>
          </td>
        </tr>
      )}
    </tbody>
  </Table>;
};

export default SuppliersTab;
